import Vue from 'vue';
import Router from 'vue-router';
import Login from '@/pages/Login';
import confirmEmail from '@/utils/confirm-email';
import Components from '@/pages/Components';
import store from './store/index';

Vue.use(Router);

const NOT_LOGGED_IN_WHITELIST = [
  'public-tender',
  'login',
  'register',
  'complete',
  'forgot',
  'confirm-email',
  'password-recovery',
  'invite',
  'public',
  'public-act-list',
  'ui',
];

const authBeforeEnter = (to, from, next) =>
  next(store.state.auth.token ? { name: 'projects' } : undefined);

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  routes: [
    {
      path: '/p/tender/:tenderHash',
      name: 'public-tender',
      component: () => import(/* webpackChunkName: "project" */ './pages/PublicTender'),
    },
    {
      path: '/ui',
      name: 'ui',
      component: Components,
      beforeEnter: authBeforeEnter,
    },
    {
      path: '/',
      name: 'login',
      component: Login,
      beforeEnter: authBeforeEnter,
    },
    {
      path: '/register',
      name: 'register',
      component: () => import(/* webpackChunkName: "register" */ './pages/Register.vue'),
    },
    {
      path: '/forgot',
      name: 'forgot',
      component: () => import(/* webpackChunkName: "forgot" */ './pages/Forgot.vue'),
      beforeEnter: authBeforeEnter,
    },
    {
      path: '/invitation/:hash',
      name: 'invite',
      component: () => import(/* webpackChunkName: "invite" */ './pages/Invite.vue'),
      beforeEnter: authBeforeEnter,
    },
    {
      path: '/contractor-register-finish/:hash',
      name: 'complete',
      component: () => import(/* webpackChunkName: "complete" */ './pages/Complete.vue'),
    },
    {
      path: '/incoming_projects',
      name: 'incoming-projects',
      component: () =>
        import(/* webpackChunkName: "incoming-projects" */ './pages/IncomingProjects.vue'),
    },
    {
      path: '/projects',
      name: 'projects',
      component: () => import(/* webpackChunkName: "projects" */ './pages/Projects.vue'),
    },
    {
      path: '/projects/:id',
      name: 'project',
      component: () => import(/* webpackChunkName: "project" */ './pages/Project.vue'),
      children: [
        {
          path: '',
          name: 'summary',
          alias: 'summary',
          component: () =>
            import(/* webpackChunkName: "summary" */ './containers/project/Summary.vue'),
        },
        {
          path: 'list/:list',
          name: 'list',
          component: () => import(/* webpackChunkName: "list" */ './containers/project/Table.vue'),
        },
        {
          path: 'tenders/:status',
          name: 'tenders',
          component: () => import(/* webpackChunkName: "tenders" */ './pages/Tenders.vue'),
        },
        {
          path: 'tender/:tenderId',
          name: 'tender',
          component: () => import(/* webpackChunkName: "tender" */ './pages/Tender.vue'),
          children: [
            {
              path: '',
              name: 'tender-summary',
              component: () =>
                import(/* webpackChunkName: "summary" */ './containers/tender/TenderSummary.vue'),
              redirect: { name: 'tender-offers' },
              children: [
                { path: '', redirect: { name: 'tender-offers' } },
                { path: '/', redirect: { name: 'tender-offers' } },
                {
                  path: 'offers',
                  name: 'tender-offers',
                  component: () =>
                    import(
                      /* webpackChunkName: "tender-offers" */ './containers/tender/TenderOffersPage'
                    ),
                },
                {
                  path: 'companies',
                  name: 'tender-companies',
                  component: () =>
                    import(
                      /* webpackChunkName: "tender-companies" */ './containers/tender/TenderCompanies.vue'
                    ),
                },
                {
                  path: 'info',
                  name: 'tender-info',
                  component: () =>
                    import(
                      /* webpackChunkName: "tender-info" */ './containers/tender/tender-info/TenderInfo.vue'
                    ),
                },
              ],
            },
            {
              path: 'list/:tenderListId',
              name: 'tender-list',
              component: () =>
                import(
                  /* webpackChunkName: "tender-info" */ './containers/tender/tender-list/TenderList.vue'
                ),
            },
          ],
        },
        {
          // Нужен только для обратной совместимости версий (хотя для чего она...?)
          path: '*',
          redirect: (to) => {
            const routeAfterProjectId = to.params?.pathMatch?.split('/')?.slice(1)?.join('/') || '';
            return {
              name: 'summary',
              params: { id: to.params.id },
              hash: `/${routeAfterProjectId}`,
            };
          },
        },
      ],
    },
    {
      path: '/incoming_tender/:incomingTenderId',
      component: () =>
        import(/* webpackChunkName: "incoming-project" */ './pages/IncomingTender.vue'),
      name: 'incoming-tender',
      children: [
        {
          path: '',
          name: 'incoming-tender-summary',
          component: () =>
            import(
              /* webpackChunkName: "summary" */ './containers/incoming-tender/TenderSummary.vue'
            ),
          redirect: { name: 'incoming-tender-info' },
          children: [
            { path: '', redirect: { name: 'incoming-tender-info' } },
            { path: '/', redirect: { name: 'incoming-tender-info' } },
            {
              path: 'offers',
              name: 'incoming-tender-offers',
              component: () =>
                import(
                  /* webpackChunkName: "tender-offers" */ './containers/incoming-tender/IncomingTenderOffersPage.vue'
                ),
            },
            {
              path: 'info',
              name: 'incoming-tender-info',
              component: () =>
                import(
                  /* webpackChunkName: "tender-info" */ './containers/incoming-tender/IncomingTenderInfo.vue'
                ),
            },
          ],
        },
        {
          path: 'list/:incomingTenderListId',
          name: 'incoming-tender-list',
          component: () =>
            import(
              /* webpackChunkName: "tender-info" */ './containers/incoming-tender/incoming-tender-list/IncomingTenderList.vue'
            ),
        },
      ],
    },
    {
      path: '/import/:id',
      component: () => import(/* webpackChunkName: "import-project" */ './pages/Import.vue'),
      name: 'import',
      props: true,
    },
    {
      path: '/import/:id/:listId',
      component: () => import(/* webpackChunkName: "import-project" */ './pages/Import.vue'),
      name: 'import-list',
      props: true,
    },
    {
      path: '/confirm-email/:hash',
      name: 'confirm-email',
      beforeEnter: confirmEmail,
    },
    {
      path: '/password-recovery/:hash',
      name: 'password-recovery',
      component: () => import(/* webpackChunkName: "recovery" */ './pages/Recovery.vue'),
    },
    {
      path: '/contractors',
      name: 'contractors',
      component: () => import(/* webpackChunkName: "recovery" */ './pages/Contractors.vue'),
    },
    {
      path: '*',
      name: '404',
      component: () => import(/* webpackChunkName: "404" */ './pages/PageNotFound'),
    },
  ],
});

router.beforeEach((to, from, next) => {
  // запрещаем вход на другие страницы для неавторизованных
  next(
    !store.state.auth.token && !NOT_LOGGED_IN_WHITELIST.includes(to.name)
      ? { name: 'login' }
      : undefined
  );
});

const originalPush = router.push;
router.push = function push(location) {
  return originalPush.call(this, location).catch(() => {});
};

export default router;
